import { DEFAULT_KYC_FLOW, KycFlow, KYC_FLOW_BASE_PATHS } from "@/app/enums/kyc-flow";

/**
 * Resolves API `kycFlow` to a known flow.
 * Unibo is only enabled in UAT when kycFlow is "unibo"; otherwise falls back to doc-vault.
 */
export function resolveKycFlow(kycFlow?: string | null): KycFlow {
    if (kycFlow === KycFlow.UNIBO) {
        return KycFlow.UNIBO;
    }
    if (kycFlow === KycFlow.DOCVAULT) {
        return KycFlow.DOCVAULT;
    }
    return DEFAULT_KYC_FLOW;
}

export function getKycBasePath(kycFlow?: string | null): string {
    return KYC_FLOW_BASE_PATHS[resolveKycFlow(kycFlow)];
}

export interface BuildKycRedirectUrlOptions {
    kycFlow?: string | null;
    redirectUrl: string;
    queryParams?: Record<string, string | number | undefined | null>;
    /** Optional origin prefix (e.g. process.env.ICM_URL) */
    baseUrl?: string;
}

/**
 * Builds a KYC entry URL with redirectUrl and optional query params.
 * Example: `/doc-vault?redirectUrl=%2Fprofile` or `/unibo?redirectUrl=...&isin=...`
 */
export function buildKycRedirectUrl({
    kycFlow,
    redirectUrl,
    queryParams = {},
    baseUrl = "",
}: BuildKycRedirectUrlOptions): string {
    const params = new URLSearchParams();
    params.set("redirectUrl", redirectUrl);

    Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
        }
    });

    const path = `${getKycBasePath(kycFlow)}?${params.toString()}`;
    if (!baseUrl) {
        return path;
    }
    return `${baseUrl.replace(/\/$/, "")}${path}`;
}

const DEFAULT_POST_KYC_REDIRECT = "/profile";

/**
 * Builds `postKycRedirectUrl` for Digio createKycRequest.
 * Backend prepends `/doc-vault?redirectUrl=` to this value.
 */
export function buildPostKycRedirectUrl(
    search: string,
    defaultRedirectUrl: string = DEFAULT_POST_KYC_REDIRECT,
): string {
    const params = new URLSearchParams(
        search.startsWith("?") || search === "" ? search : `?${search}`,
    );
    const redirectUrl = params.get("redirectUrl")?.trim() || defaultRedirectUrl;
    params.delete("redirectUrl");
    const otherParams = params.toString();
    const encodedRedirect = encodeURIComponent(redirectUrl);
    return otherParams ? `${encodedRedirect}&${otherParams}` : encodedRedirect;
}

/** True when URL includes KYC entry context (redirectUrl, isin, or units). */
export function hasKycEntryQueryParams(searchParams: URLSearchParams): boolean {
    return (
        searchParams.has("redirectUrl") ||
        searchParams.has("isin") ||
        searchParams.has("units")
    );
}

/**
 * Resolves post-KYC / drop-off redirect targets (handles UE external URLs).
 */
export function resolveKycRedirectDestination(redirectUrl: string): string {
    if (redirectUrl.includes("unlistedequities")) {
        return `${process.env.ICM_UE_URL}${redirectUrl}`;
    }
    return redirectUrl;
}
